// First-party visitor identity.
//
// Why this exists: the backend previously identified anonymous visitors by
// hashing their IP address. On mobile carrier NAT (and office/cafe Wi-Fi)
// hundreds of real people share a single egress IP, so they all collapsed into
// one "visitor" — which is why ad click counts dwarfed our visitor numbers.
//
// A random per-browser ID fixes that. It carries no personal data and is only
// meaningful to us.

const STORAGE_KEY = 'pfVisitorId';

let cached = null;

function generateId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Returns a stable per-browser visitor ID, creating one on first call.
 *
 * Persists to localStorage so the ID survives across sessions. Falls back to
 * sessionStorage, then to an in-memory value, so private browsing and blocked
 * storage degrade gracefully instead of throwing. When nothing can be stored
 * the backend still falls back to its IP hash.
 */
export function getVisitorId() {
  if (cached) return cached;

  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = generateId();
      localStorage.setItem(STORAGE_KEY, id);
    }
    cached = id;
    return id;
  } catch (_) {
    /* localStorage unavailable — try sessionStorage */
  }

  try {
    let id = sessionStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(STORAGE_KEY, id);
    }
    cached = id;
    return id;
  } catch (_) {
    /* storage fully blocked — keep it in memory for this page load only */
  }

  cached = generateId();
  return cached;
}

/** Clears the stored visitor ID (used when consent is withdrawn). */
export function clearVisitorId() {
  cached = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}
